import { useEffect, useRef, useState, type PointerEvent, type RefObject } from 'react';

/**
 * Hand-built SVG charts in the Elena OS style: one brass series, hairline grid,
 * 2px line with a 10% wash, 4px rounded bar ends, crosshair / per-bar tooltips.
 */

function useWidth<T extends HTMLElement>(): [RefObject<T | null>, number] {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setWidth(Math.floor(entry?.contentRect.width ?? 0)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

/** Clean y-axis maximum and tick step for small integer counts. */
export function niceScale(max: number): { top: number; step: number } {
  if (max <= 4) return { top: 4, step: 1 };
  const rough = max / 4;
  const pow = 10 ** Math.floor(Math.log10(rough));
  const step = [1, 2, 5, 10].map((m) => m * pow).find((s) => s >= rough) ?? 10 * pow;
  return { top: Math.ceil(max / step) * step, step };
}

const hhmm = (t: number) =>
  new Date(t).toLocaleTimeString('sr-Latn-RS', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Belgrade',
  });

const PAD = { top: 12, right: 12, bottom: 26, left: 32 };

/** Players over the last 24 h. Gaps (server offline or no data) are shaded, not interpolated. */
export function PlayersLine({
  series,
  label,
}: {
  series: Array<[number, number | null]>;
  label: string;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const height = 220;
  const w = Math.max(0, width - PAD.left - PAD.right);
  const h = height - PAD.top - PAD.bottom;
  const values = series.map(([, v]) => v ?? 0);
  const { top, step } = niceScale(Math.max(1, ...values));
  const n = series.length;
  const x = (i: number) => PAD.left + (n <= 1 ? 0 : (i / (n - 1)) * w);
  const y = (v: number) => PAD.top + h - (v / top) * h;

  // Split into continuous runs so offline stretches break the line.
  const runs: Array<Array<[number, number]>> = [];
  let current: Array<[number, number]> = [];
  series.forEach(([, v], i) => {
    if (v === null) {
      if (current.length) runs.push(current);
      current = [];
    } else current.push([i, v]);
  });
  if (current.length) runs.push(current);

  const gaps: Array<[number, number]> = [];
  let gapStart: number | null = null;
  series.forEach(([, v], i) => {
    if (v === null && gapStart === null) gapStart = i;
    if (v !== null && gapStart !== null) {
      gaps.push([gapStart, i]);
      gapStart = null;
    }
  });
  if (gapStart !== null) gaps.push([gapStart, n - 1]);

  const ticks: number[] = [];
  for (let v = 0; v <= top; v += step) ticks.push(v);
  const xTicks = series
    .map(([t], i) => ({ t, i }))
    .filter(({ t }) => /^(00|06|12|18):00$/.test(hhmm(t)));

  const onMove = (e: PointerEvent<SVGRectElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const i = Math.round(((e.clientX - rect.left) / rect.width) * (n - 1));
    setHover(Math.max(0, Math.min(n - 1, i)));
  };

  const hv = hover !== null ? series[hover] : undefined;

  return (
    <div className="chart" ref={ref}>
      {width > 0 && n > 1 && (
        <svg width={width} height={height} role="img" aria-label={label}>
          {ticks.map((v) => (
            <g key={v}>
              <line className="chart-grid" x1={PAD.left} x2={PAD.left + w} y1={y(v)} y2={y(v)} />
              <text className="chart-axis" x={PAD.left - 8} y={y(v) + 4} textAnchor="end">
                {v}
              </text>
            </g>
          ))}
          {xTicks.map(({ t, i }) => (
            <text key={t} className="chart-axis" x={x(i)} y={height - 6} textAnchor="middle">
              {hhmm(t)}
            </text>
          ))}
          {gaps.map(([a, b]) => (
            <rect
              key={a}
              className="chart-gap"
              x={x(a)}
              y={PAD.top}
              width={Math.max(2, x(b) - x(a))}
              height={h}
            />
          ))}
          {runs.map((run) => {
            const line = run.map(([i, v], k) => `${k ? 'L' : 'M'}${x(i)},${y(v)}`).join(' ');
            const area = `${line} L${x(run.at(-1)![0])},${y(0)} L${x(run[0]![0])},${y(0)} Z`;
            return (
              <g key={run[0]![0]}>
                <path className="chart-area" d={area} />
                <path className="chart-line" d={line} />
              </g>
            );
          })}
          {hover !== null && hv && (
            <g pointerEvents="none">
              <line
                className="chart-cross"
                x1={x(hover)}
                x2={x(hover)}
                y1={PAD.top}
                y2={PAD.top + h}
              />
              {hv[1] !== null && <circle className="chart-dot" cx={x(hover)} cy={y(hv[1])} r={5} />}
            </g>
          )}
          <rect
            x={PAD.left}
            y={PAD.top}
            width={w}
            height={h}
            fill="transparent"
            onPointerMove={onMove}
            onPointerLeave={() => setHover(null)}
          />
        </svg>
      )}
      {hover !== null && hv && (
        <div
          className="chart-tip"
          style={{ left: Math.min(Math.max(x(hover), 70), width - 70), top: 4 }}
          role="status"
        >
          <span className="muted small">{hhmm(hv[0])}</span>
          <strong>{hv[1] === null ? 'nema podataka' : `${hv[1]} igrača`}</strong>
        </div>
      )}
    </div>
  );
}

/** Average players per Belgrade hour; the quietest hour is called out for restarts. */
export function HourlyBars({
  hourly,
  best,
  label,
}: {
  hourly: Array<number | null>;
  best: number | null;
  label: string;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const height = 200;
  const w = Math.max(0, width - PAD.left - PAD.right);
  const h = height - PAD.top - PAD.bottom;
  const { top, step } = niceScale(Math.max(1, ...hourly.map((v) => v ?? 0)));
  const band = w / 24;
  const barW = Math.min(24, Math.max(4, band - 4));
  const y = (v: number) => PAD.top + h - (v / top) * h;
  const ticks: number[] = [];
  for (let v = 0; v <= top; v += step) ticks.push(v);

  const bar = (i: number, v: number) => {
    const x0 = PAD.left + i * band + (band - barW) / 2;
    const y0 = y(v);
    const r = Math.min(4, barW / 2, PAD.top + h - y0);
    const bottom = PAD.top + h;
    // Rounded top, square at the baseline.
    return `M${x0},${bottom} L${x0},${y0 + r} Q${x0},${y0} ${x0 + r},${y0} L${x0 + barW - r},${y0} Q${x0 + barW},${y0} ${x0 + barW},${y0 + r} L${x0 + barW},${bottom} Z`;
  };

  const hv = hover !== null ? hourly[hover] : undefined;

  return (
    <div className="chart" ref={ref}>
      {width > 0 && (
        <svg width={width} height={height} role="img" aria-label={label}>
          {ticks.map((v) => (
            <g key={v}>
              <line className="chart-grid" x1={PAD.left} x2={PAD.left + w} y1={y(v)} y2={y(v)} />
              <text className="chart-axis" x={PAD.left - 8} y={y(v) + 4} textAnchor="end">
                {v}
              </text>
            </g>
          ))}
          {hourly.map((v, i) =>
            v === null || v === 0 ? null : (
              <path
                key={i}
                d={bar(i, v)}
                className={`chart-bar${i === best ? ' is-best' : ''}${hover === i ? ' is-hover' : ''}`}
              />
            ),
          )}
          {best !== null && (
            <text
              className="chart-best-label"
              x={PAD.left + best * band + band / 2}
              y={y(hourly[best] ?? 0) - 8}
              textAnchor="middle"
            >
              restart
            </text>
          )}
          {[0, 6, 12, 18].map((hr) => (
            <text
              key={hr}
              className="chart-axis"
              x={PAD.left + hr * band + band / 2}
              y={height - 6}
              textAnchor="middle"
            >
              {String(hr).padStart(2, '0')}h
            </text>
          ))}
          {hourly.map((_, i) => (
            <rect
              key={i}
              x={PAD.left + i * band}
              y={PAD.top}
              width={band}
              height={h}
              fill="transparent"
              onPointerEnter={() => setHover(i)}
              onPointerLeave={() => setHover(null)}
            >
              <title>{`${String(i).padStart(2, '0')}:00 · prosek ${hourly[i] ?? '—'}`}</title>
            </rect>
          ))}
        </svg>
      )}
      {hover !== null && (
        <div
          className="chart-tip"
          style={{
            left: Math.min(Math.max(PAD.left + hover * band + band / 2, 70), width - 70),
            top: 4,
          }}
          role="status"
        >
          <span className="muted small">
            {String(hover).padStart(2, '0')}:00–{String((hover + 1) % 24).padStart(2, '0')}:00
          </span>
          <strong>
            {hv === null || hv === undefined
              ? 'nema podataka'
              : `prosek ${String(hv).replace('.', ',')}`}
          </strong>
          {hover === best && <span className="small chart-tip-best">najbolje za restart</span>}
        </div>
      )}
    </div>
  );
}
